/**
 * LIVEL — bendro pristatymo būdo (Omniva / LP EXPRESS) bloko orkestratorius
 * (2026-09-08 UI/UX pertvarka).
 *
 * SVARBU: šis failas NETURI jokios vežėjo-specifinės logikos — jokio
 * paštomatų/terminalų fetch, jokios paieškos, jokio /cart/update.js
 * kvietimo su livel_delivery_* reikšmėmis, jokio checkout autofill. Visa
 * tai lieka TIK cart-omniva.js / cart-lpexpress.js viduje, nepakeista. Šis
 * failas tik:
 *   1) rodo/slepia po vieną vežėjo panelę pagal pasirinktą radio kortelę;
 *   2) perjungus vežėją, IŠ NAUJO NEDUBLIUOJA clearSelection() logikos —
 *      tiesiog paspaudžia (root.click()) anksčiau aktyvaus vežėjo JAU
 *      ESANTĮ "Keisti paštomatą" mygtuką, jei tas vežėjas turėjo aktyvų
 *      pasirinkimą (taip livel_delivery_carrier/_locker_* išvalomi per tą
 *      pačią, nepakeistą funkciją, kurią jau naudoja "Keisti" mygtukas);
 *   3) rodo VIENĄ bendrą "iki nemokamo pristatymo" pranešimą — naudoja TĄ
 *      PATĮ viešą /pricing endpoint'ą ir tą pačią ribos reikšmę, kurią prieš
 *      tai atskirai skaitė cart-omniva.js/cart-lpexpress.js (žr. ten
 *      getPricing() — identiška logika, tik konsoliduota vienoje vietoje).
 *
 * Pasirinkimo/išvalymo įvykiai iš cart-omniva.js/cart-lpexpress.js atkeliauja
 * per bendrą 'livel:delivery-selection-changed' CustomEvent (žr. tuos failus,
 * broadcastDeliverySelection()) — jo detail dabar papildomai neša visą šviežią
 * `cart` objektą, kad čia nereikėtų papildomo /cart.js fetch.
 */

const PRICING_ENDPOINT = 'https://livel-parcel-lockers.dianakatharina64.workers.dev/pricing';

/** @type {{ threshold: number | null } | null} */
let pricingCache = null;

function euros(cents) {
  return (cents / 100).toLocaleString('lt-LT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

async function getPricing() {
  if (pricingCache) return pricingCache;
  try {
    const res = await fetch(PRICING_ENDPOINT, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('Pricing endpoint failed: ' + res.status);
    const data = await res.json();
    const threshold = Number.isFinite(data.freeShippingThreshold)
      ? data.freeShippingThreshold
      : parseFloat(data.freeShippingThreshold);
    pricingCache = { threshold: Number.isFinite(threshold) ? Math.round(threshold * 100) : null };
  } catch (e) {
    pricingCache = { threshold: null };
  }
  return pricingCache;
}

async function refreshSharedFreeShipping(root, cartMaybe) {
  const messageEl = root.querySelector('[data-cart-delivery-free-shipping]');
  if (!messageEl) return;

  const pricing = await getPricing();
  if (pricing.threshold == null) {
    messageEl.textContent = '';
    return;
  }

  let cart = cartMaybe;
  if (!cart) {
    cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
  }

  // total_price = po nuolaidos kodų — taip pat skaičiuoja Shopify free-shipping nuolaida (nuo 30 €).
  const subtotal = cart.total_price;

  messageEl.classList.toggle('is-reached', subtotal >= pricing.threshold);
  if (subtotal >= pricing.threshold) {
    messageEl.textContent = root.dataset.freeShippingReachedText;
  } else {
    const remaining = pricing.threshold - subtotal;
    messageEl.textContent = root.dataset.freeShippingProgressText.replace('{amount}', euros(remaining));
  }
}

// LIVEL: Omniva/LP EXPRESS kortelės rodo savo statinę kainą (užrašyta Liquid
// per data-price-label — žr. cart-delivery-widget.liquid), o pasiekus laisvo
// pristatymo ribą — "Nemokamai". Riba/subtotal skaičiavimas NAUDOJA TĄ PATĮ
// getPricing() ir tą pačią subtotal logiką kaip refreshSharedFreeShipping()
// aukščiau. Kurjerio kortelė TYČIA neturi [data-cart-delivery-price] žymos
// (žr. cart-delivery-widget.liquid) — Kurjeris visada kainuoja 3,95 €,
// nepriklausomai nuo krepšelio sumos, tad ji čia neperrašoma į "Nemokamai".
async function refreshPriceLabels(root, cartMaybe) {
  const priceEls = root.querySelectorAll('[data-cart-delivery-price]');
  if (!priceEls.length) return;

  const pricing = await getPricing();
  if (pricing.threshold == null) return;

  let cart = cartMaybe;
  if (!cart) {
    cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
  }

  // total_price = po nuolaidos kodų — taip pat skaičiuoja Shopify free-shipping nuolaida (nuo 30 €).
  const subtotal = cart.total_price;
  const freeShippingReached = subtotal >= pricing.threshold;

  priceEls.forEach((el) => {
    el.textContent = freeShippingReached ? 'Nemokamai' : el.dataset.priceLabel;
  });
}

function showPanel(root, carrier) {
  const omnivaPanel = root.querySelector('[data-cart-panel="Omniva"]');
  const lpexpressPanel = root.querySelector('[data-cart-panel="LP EXPRESS"]');
  const courierPanel = root.querySelector('[data-cart-panel="Kurjeris"]');
  if (omnivaPanel) omnivaPanel.hidden = carrier !== 'Omniva';
  if (lpexpressPanel) lpexpressPanel.hidden = carrier !== 'LP EXPRESS';
  if (courierPanel) courierPanel.hidden = carrier !== 'Kurjeris';
}

function wireSwitcher(root) {
  const radios = root.querySelectorAll('[data-cart-delivery-radio]');
  if (!radios.length) return;

  radios.forEach((radio) => {
    radio.addEventListener('change', () => {
      if (!radio.checked) return;
      const newCarrier = radio.value;
      showPanel(root, newCarrier);

      // Jei kuris nors KITAS paštomato vežėjas (Omniva/LP EXPRESS — Kurjeris
      // neturi savo "Keisti" mygtuko, jam skirta atskira cart-courier.js
      // logika) turėjo pasirinktą paštomatą, išvalome jį paspausdami jo jau
      // esantį "Keisti" mygtuką (žr. failo viršuje esantį komentarą — taip
      // naudojame tą pačią, nepakeistą clearSelection() logiką kiekviename
      // atskirame faile). Iteruojame per VISUS kitus vežėjus (ne tik vieną),
      // nes nuo Kurjerio pridėjimo galimų "kitų" gali būti daugiau nei vienas.
      ['Omniva', 'LP EXPRESS'].forEach((otherCarrier) => {
        if (otherCarrier === newCarrier) return;
        const otherPanel = root.querySelector(`[data-cart-panel="${otherCarrier}"]`);
        if (!otherPanel) return;
        const otherSelectedEl = otherPanel.querySelector('[data-cart-omniva-selected], [data-cart-lpexpress-selected]');
        const otherWasSelected = otherSelectedEl && !otherSelectedEl.hidden;
        if (otherWasSelected) {
          const changeBtn = otherPanel.querySelector('[data-cart-omniva-change], [data-cart-lpexpress-change]');
          if (changeBtn) changeBtn.click();
        }
      });
    });
  });
}

async function initCartDeliveryAsync(root) {
  const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
  await refreshSharedFreeShipping(root, cart);
  await refreshPriceLabels(root, cart);
}

function initCartDelivery(root) {
  if (root.dataset.cartDeliveryInitialized) return;
  root.dataset.cartDeliveryInitialized = 'true';

  wireSwitcher(root);
  initCartDeliveryAsync(root);
}

function initAll() {
  document.querySelectorAll('[data-cart-delivery]').forEach(initCartDelivery);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}

document.addEventListener('shopify:section:load', initAll);

// Tas pats MutationObserver re-init principas kaip cart-omniva.js/
// cart-lpexpress.js (žr. tuos failus dėl pilno AJAX/innerHTML script-execution
// root-cause paaiškinimo) — būtina, kad šis blokas teisingai veiktų desktop
// cart drawer'yje po AJAX cart atnaujinimo.
const cartDeliveryObserver = new MutationObserver(() => {
  const unwired = document.querySelector('[data-cart-delivery]:not([data-cart-delivery-initialized])');
  if (unwired) initAll();
});
cartDeliveryObserver.observe(document.body, { childList: true, subtree: true });

// Bendras "iki nemokamo pristatymo" pranešimas atnaujinamas kaskart, kai bet
// kuris vežėjo widget'as išsaugo/išvalo pasirinkimą.
document.addEventListener('livel:delivery-selection-changed', (event) => {
  const detail = event.detail || {};
  document.querySelectorAll('[data-cart-delivery]').forEach((root) => {
    refreshSharedFreeShipping(root, detail.cart);
    refreshPriceLabels(root, detail.cart);
  });
});

// LIVEL (2026-10-02): privalomas paštomato pasirinkimas prieš checkout.
// Jei aktyvus vežėjas yra Omniva arba LP EXPRESS, bet konkretus paštomatas
// dar nepasirinktas (vežėjo "pasirinkta" būsena paslėpta), "Atsiskaityti"
// paspaudimas (ir formos submit su checkout mygtuku) sustabdomas, o prie
// paštomato paieškos rodoma inline klaida. Kurjeriui jokio reikalavimo nėra.
// Vežėjų failų (cart-omniva.js/cart-lpexpress.js) logika nekeičiama — čia tik
// skaitoma jų DOM būsena.
const CHECKOUT_BUTTON_SELECTOR = '.cart__checkout-button, button[name="checkout"]';

function getActiveCarrier(root) {
  const checked = root.querySelector('[data-cart-delivery-radio]:checked');
  if (checked) return checked.value;
  // Kai įjungtas tik vienas vežėjas, perjungiklio nėra — imame matomą panelę.
  const visiblePanel = root.querySelector('[data-cart-omniva]:not([hidden]), [data-cart-lpexpress]:not([hidden])');
  return visiblePanel ? visiblePanel.dataset.cartPanel : '';
}

function isLockerMissing(root) {
  const carrier = getActiveCarrier(root);
  if (carrier !== 'Omniva' && carrier !== 'LP EXPRESS') return false;
  const panel = root.querySelector(`[data-cart-panel="${carrier}"]`);
  if (!panel) return false;
  const selectedEl = panel.querySelector('[data-cart-omniva-selected], [data-cart-lpexpress-selected]');
  return !selectedEl || selectedEl.hidden;
}

function findDeliveryRoot(button) {
  const scope = button.closest('cart-drawer-component, dialog, .shopify-section');
  return (scope && scope.querySelector('[data-cart-delivery]')) || document.querySelector('[data-cart-delivery]');
}

function setDeliveryError(root, show) {
  root.classList.toggle('cart-delivery--error', show);
  const errorEl = root.querySelector('[data-cart-delivery-error]');
  // Tik keičiant reikšmę — kitaip MutationObserver žemiau suktųsi ratu.
  if (errorEl && errorEl.hidden === show) errorEl.hidden = !show;
}

// Mygtukas tik vizualiai prigesinamas (aria-disabled, ne `disabled`), kad
// paspaudimas vis tiek pasiektų validaciją ir parodytų klaidą.
function syncCheckoutState() {
  document.querySelectorAll(CHECKOUT_BUTTON_SELECTOR).forEach((button) => {
    const root = findDeliveryRoot(button);
    const blocked = Boolean(root) && isLockerMissing(root);
    button.classList.toggle('cart__checkout-button--locker-missing', blocked);
    if (blocked) {
      button.setAttribute('aria-disabled', 'true');
    } else if (button.getAttribute('aria-disabled') === 'true') {
      button.removeAttribute('aria-disabled');
    }
    if (root && !isLockerMissing(root)) setDeliveryError(root, false);
  });
}

function blockCheckoutIfLockerMissing(event, button) {
  const root = findDeliveryRoot(button);
  if (!root || !isLockerMissing(root)) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  setDeliveryError(root, true);
  syncCheckoutState();
  root.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

document.addEventListener(
  'click',
  (event) => {
    const button = event.target.closest && event.target.closest(CHECKOUT_BUTTON_SELECTOR);
    if (button) blockCheckoutIfLockerMissing(event, button);
  },
  true
);

// Antra apsauga — jei forma pateikiama ne pelės paspaudimu (pvz. Enter).
document.addEventListener(
  'submit',
  (event) => {
    const submitter = event.submitter;
    if (submitter && submitter.matches(CHECKOUT_BUTTON_SELECTOR)) {
      blockCheckoutIfLockerMissing(event, submitter);
    }
  },
  true
);

document.addEventListener('change', (event) => {
  if (!event.target.matches || !event.target.matches('[data-cart-delivery-radio]')) return;
  const root = event.target.closest('[data-cart-delivery]');
  if (root) setDeliveryError(root, false);
  syncCheckoutState();
});

document.addEventListener('livel:delivery-selection-changed', () => {
  // Vežėjo failas jau pritaikė "pasirinkta" būseną prieš siųsdamas įvykį.
  syncCheckoutState();
});

// Krepšelio perpiešimas (AJAX) pakeičia mygtuką/bloką naujais elementais, o
// vežėjų failai inicializacijos metu perjungia "pasirinkta" būsenos `hidden` atributą.
const checkoutStateObserver = new MutationObserver(() => syncCheckoutState());
checkoutStateObserver.observe(document.body, {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: ['hidden']
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', syncCheckoutState);
} else {
  syncCheckoutState();
}
